import PropTypes from 'prop-types';
import { noop } from 'lodash/fp';
import classnames from 'classnames/bind';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Loading, LoadingSize } from 'app/components/core/Loading';
import * as styles from './ConfirmationButton.css';

const cx = classnames.bind(styles);

export function ConfirmationButton({
    testId = null,
    className = null,
    label,
    hint,
    icon = null,
    disabled = false,
    isProcessing = false,
    onClick = noop,
    ...props
}) {
    const classes = cx('base', className);

    return (
        <button
            data-testid={testId}
            className={classes}
            aria-live="true"
            aria-busy={isProcessing}
            aria-disabled={disabled || isProcessing}
            aria-label={label}
            {...props}>
            <div className={styles.fill} onAnimationEnd={onClick} />
            <div className={styles.label}>
                {icon && <Icon variant={icon} size={IconSize.EXTRA_SMALL} />}
                {label}
            </div>
            <div className={styles.hint}>
                <Icon variant="info" size={IconSize.EXTRA_SMALL} />
                {hint}
            </div>
            {isProcessing && (
                <div className={styles.loading}>
                    <Loading size={LoadingSize.SMALL} testId="processing-button" />
                </div>
            )}
        </button>
    );
}

ConfirmationButton.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * An optional icon variant to display to the left of the button text.
     */
    icon: PropTypes.string,

    /**
     * Label displayed in the button. Unlike `Button`, label content is not
     * provided via the `children` prop.
     */
    label: PropTypes.string.isRequired,

    /**
     * A hint to show on hover. This should instruct the user to "Press & Hold"
     * on the button to execute the action, with copy varying to suit the given
     * action.
     */
    hint: PropTypes.string.isRequired,

    /**
     * Prevent the user from interacting with the component. If this prop isn't
     * set, the component can still be disabled from a containing element, for
     * example a `<fieldset>`.
     */
    disabled: PropTypes.bool,

    /**
     * Hides the text and shows a processing animation to indicate something in
     * progress. The button will be disabled while this is true, but won't have
     * the default disabled appearance.
     */
    isProcessing: PropTypes.bool,

    /**
     * Callback triggered when clicking the button. Only fired after the progress
     * animation has finished.
     */
    onClick: PropTypes.func
};
